import { useCallback, useMemo, useState } from 'react';

import { Alert, Box, Button, FormControlLabel, MenuItem, Paper, Stack, Switch, Tab, Tabs, TextField, Typography } from '@mui/material';
import { ThemeOptions, ThemeProvider, createTheme } from '@mui/material/styles';

import { useToastService } from '@@common';
import { CopyToClipboardButton, PasteFromClipboardButton } from '@@components';

type ThemeDefinition = Record<string, any>;

type ThemeDesignerProps = {
	theme: ThemeDefinition;
	setTheme: (theme: ThemeDefinition) => void;
	name?: string;
	setName?: (name: string) => void;
	isDefault?: boolean;
	setDefault?: () => void;
};

type ThemeDesignerTab = 'design' | 'presets' | 'preview';

type ColorFieldDefinition = {
	label: string;
	path: string[];
};

const SEMANTIC_COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'];

const SURFACE_COLORS: ColorFieldDefinition[] = [
	{ label: 'Page background', path: ['palette', 'background', 'default'] },
	{ label: 'Paper background', path: ['palette', 'background', 'paper'] },
	{ label: 'Primary text', path: ['palette', 'text', 'primary'] },
	{ label: 'Secondary text', path: ['palette', 'text', 'secondary'] },
	{ label: 'Disabled text', path: ['palette', 'text', 'disabled'] },
	{ label: 'Divider', path: ['palette', 'divider'] },
];

const ACTION_COLORS: ColorFieldDefinition[] = [
	{ label: 'Active', path: ['palette', 'action', 'active'] },
	{ label: 'Hover', path: ['palette', 'action', 'hover'] },
	{ label: 'Selected', path: ['palette', 'action', 'selected'] },
	{ label: 'Disabled', path: ['palette', 'action', 'disabled'] },
	{ label: 'Disabled background', path: ['palette', 'action', 'disabledBackground'] },
	{ label: 'Focus', path: ['palette', 'action', 'focus'] },
];

const TYPOGRAPHY_VARIANTS = [
	{ key: 'h1', label: 'Heading 1' },
	{ key: 'h2', label: 'Heading 2' },
	{ key: 'h3', label: 'Heading 3' },
	{ key: 'h4', label: 'Heading 4' },
	{ key: 'h5', label: 'Heading 5' },
	{ key: 'h6', label: 'Heading 6' },
	{ key: 'body1', label: 'Body' },
	{ key: 'body2', label: 'Secondary body' },
	{ key: 'button', label: 'Button text' },
	{ key: 'caption', label: 'Caption' },
];

type ThemePreset = {
	name: string;
	description: string;
	colors: string[];
	preview: {
		background: string;
		surface: string;
		text: string;
		mutedText: string;
	};
	definition: ThemeDefinition;
};

const THEME_PRESETS: ThemePreset[] = [
	{
		name: 'Clean light',
		description: 'Neutral surfaces with a blue primary action.',
		colors: ['#1976d2', '#9c27b0', '#f6f8fb'],
		preview: { background: '#f6f8fb', surface: '#ffffff', text: '#1f2937', mutedText: '#4b5563' },
		definition: {
			palette: {
				mode: 'light',
				primary: { main: '#1976d2', light: '#42a5f5', dark: '#1565c0', contrastText: '#ffffff' },
				secondary: { main: '#9c27b0', light: '#ba68c8', dark: '#7b1fa2', contrastText: '#ffffff' },
				background: { default: '#f6f8fb', paper: '#ffffff' },
				text: { primary: '#1f2937', secondary: '#4b5563' },
			},
			shape: { borderRadius: 8 },
		},
	},
	{
		name: 'Ocean',
		description: 'A cool, professional palette with clear contrast.',
		colors: ['#006e8a', '#5b5fc7', '#edf8fb'],
		preview: { background: '#edf8fb', surface: '#ffffff', text: '#12313b', mutedText: '#48616a' },
		definition: {
			palette: {
				mode: 'light',
				primary: { main: '#006e8a', light: '#2b9bb7', dark: '#004c61', contrastText: '#ffffff' },
				secondary: { main: '#5b5fc7', light: '#7b7fdb', dark: '#3f4296', contrastText: '#ffffff' },
				background: { default: '#edf8fb', paper: '#ffffff' },
				text: { primary: '#12313b', secondary: '#48616a' },
			},
			shape: { borderRadius: 10 },
		},
	},
	{
		name: 'Midnight',
		description: 'Dark surfaces with accessible blue and pink actions.',
		colors: ['#90caf9', '#f48fb1', '#121212'],
		preview: { background: '#121212', surface: '#1e1e1e', text: '#f5f5f5', mutedText: '#c7c7c7' },
		definition: {
			palette: {
				mode: 'dark',
				primary: { main: '#90caf9', light: '#bbdefb', dark: '#42a5f5', contrastText: '#0d1b2a' },
				secondary: { main: '#f48fb1', light: '#f8bbd0', dark: '#ec407a', contrastText: '#2b1020' },
				background: { default: '#121212', paper: '#1e1e1e' },
				text: { primary: '#f5f5f5', secondary: '#c7c7c7' },
			},
			shape: { borderRadius: 8 },
		},
	},
	{
		name: 'Forest',
		description: 'Calm green surfaces with a rich plum accent.',
		colors: ['#1d746b', '#7a427b', '#f3faf6'],
		preview: { background: '#f3faf6', surface: '#ffffff', text: '#173d37', mutedText: '#52716b' },
		definition: {
			palette: {
				mode: 'light',
				primary: { main: '#1d746b', light: '#4f9f94', dark: '#125249', contrastText: '#ffffff' },
				secondary: { main: '#7a427b', light: '#a16ca1', dark: '#542e55', contrastText: '#ffffff' },
				background: { default: '#f3faf6', paper: '#ffffff' },
				text: { primary: '#173d37', secondary: '#52716b' },
			},
			shape: { borderRadius: 8 },
		},
	},
	{
		name: 'Ember',
		description: 'Warm editorial energy balanced by cool teal.',
		colors: ['#b43c48', '#287f78', '#fff7f5'],
		preview: { background: '#fff7f5', surface: '#ffffff', text: '#4b2027', mutedText: '#77545a' },
		definition: {
			palette: {
				mode: 'light',
				primary: { main: '#b43c48', light: '#d76a73', dark: '#7e2731', contrastText: '#ffffff' },
				secondary: { main: '#287f78', light: '#60a8a1', dark: '#195952', contrastText: '#ffffff' },
				background: { default: '#fff7f5', paper: '#ffffff' },
				text: { primary: '#4b2027', secondary: '#77545a' },
			},
			shape: { borderRadius: 6 },
		},
	},
	{
		name: 'Orchid ink',
		description: 'Quiet violet foundations with a precise teal signal.',
		colors: ['#694caf', '#247b77', '#f8f7fc'],
		preview: { background: '#f8f7fc', surface: '#ffffff', text: '#302548', mutedText: '#665d77' },
		definition: {
			palette: {
				mode: 'light',
				primary: { main: '#694caf', light: '#9274ce', dark: '#4a337d', contrastText: '#ffffff' },
				secondary: { main: '#247b77', light: '#57a7a0', dark: '#155651', contrastText: '#ffffff' },
				background: { default: '#f8f7fc', paper: '#ffffff' },
				text: { primary: '#302548', secondary: '#665d77' },
			},
			shape: { borderRadius: 8 },
		},
	},
];

const isRecord = (value: unknown): value is ThemeDefinition => typeof value === 'object' && value !== null && !Array.isArray(value);

function getNestedValue(source: ThemeDefinition, path: string[]): unknown {
	return path.reduce<unknown>((value, key) => (isRecord(value) ? value[key] : undefined), source);
}

function setNestedValue(source: ThemeDefinition, path: string[], value: unknown): ThemeDefinition {
	const result = { ...source };
	let target = result;

	path.forEach((key, index) => {
		if (index === path.length - 1) {
			if (value === undefined || value === '') {
				delete target[key];
			} else {
				target[key] = value;
			}
			return;
		}

		const child = isRecord(target[key]) ? { ...target[key] } : {};
		target[key] = child;
		target = child;
	});

	return result;
}

function mergeTheme(source: ThemeDefinition, update: ThemeDefinition): ThemeDefinition {
	const result = { ...source };

	for (const [key, value] of Object.entries(update)) {
		result[key] = isRecord(value) && isRecord(source[key]) ? mergeTheme(source[key], value) : value;
	}

	return result;
}

function isHexColor(value: string): boolean {
	return /^#[0-9a-f]{6}$/i.test(value);
}

type ThemeColorFieldProps = {
	label: string;
	value: string;
	onChange: (value: string) => void;
};

function ThemeColorField({ label, value, onChange }: ThemeColorFieldProps) {
	return (
		<Stack direction="row" spacing={1} alignItems="center">
			<Box
				component="input"
				type="color"
				aria-label={`${label} color picker`}
				value={isHexColor(value) ? value : '#000000'}
				onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
				sx={{ width: 38, height: 38, p: 0.25, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', cursor: 'pointer' }}
			/>
			<TextField
				label={label}
				value={value}
				onChange={(event) => onChange(event.target.value)}
				placeholder="#1976d2, rgb(...), or CSS color"
				size="small"
				fullWidth
			/>
		</Stack>
	);
}

type SectionProps = React.PropsWithChildren<{
	title: string;
	description?: string;
}>;

function Section({ title, description, children }: SectionProps) {
	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Stack spacing={2}>
				<Box>
					<Typography variant="subtitle1" fontWeight={600}>
						{title}
					</Typography>
					{description && (
						<Typography variant="body2" color="text.secondary">
							{description}
						</Typography>
					)}
				</Box>
				{children}
			</Stack>
		</Paper>
	);
}

export function ThemeDesigner({ theme, setTheme, name, setName, isDefault = false, setDefault }: ThemeDesignerProps) {
	const toastService = useToastService();
	const [jsonError, setJsonError] = useState<string>();
	const [activeTab, setActiveTab] = useState<ThemeDesignerTab>('design');

	const previewTheme = useMemo(() => {
		try {
			return createTheme(theme as ThemeOptions);
		} catch {
			return createTheme();
		}
	}, [theme]);

	const updateValue = useCallback(
		(path: string[], value: unknown) => {
			setTheme(setNestedValue(theme, path, value));
		},
		[theme, setTheme],
	);

	const getStringValue = useCallback(
		(path: string[]) => {
			const value = getNestedValue(theme, path);
			return typeof value === 'string' ? value : '';
		},
		[theme],
	);

	const getNumberValue = useCallback(
		(path: string[]) => {
			const value = getNestedValue(theme, path);
			return typeof value === 'number' ? value : '';
		},
		[theme],
	);

	const getTextValue = useCallback(
		(path: string[]) => {
			const value = getNestedValue(theme, path);
			return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
		},
		[theme],
	);

	const getBooleanValue = useCallback((path: string[]) => getNestedValue(theme, path) === true, [theme]);

	const handleApplyPreset = useCallback(
		(preset: ThemePreset) => {
			setTheme(mergeTheme(theme, preset.definition));
			toastService.success(`${preset.name} preset applied`);
		},
		[setTheme, theme, toastService],
	);

	const applyPastedTheme = useCallback(
		(text: string, successMessage?: string) => {
			try {
				const parsed = JSON.parse(text) as unknown;
				if (!isRecord(parsed)) {
					setJsonError('Theme JSON must contain an object.');
					return false;
				}

				setJsonError(undefined);
				setTheme(parsed);
				if (successMessage) {
					toastService.success(successMessage);
				}
				return true;
			} catch {
				setJsonError('Theme definition is not valid JSON.');
				return false;
			}
		},
		[setTheme, toastService],
	);

	const handlePaste = useCallback(async () => {
		try {
			const clipboardText = await navigator.clipboard.readText();
			applyPastedTheme(clipboardText, 'Theme pasted from clipboard');
		} catch {
			setJsonError('Could not read from the clipboard.');
			toastService.error('Failed to paste theme from clipboard');
		}
	}, [applyPastedTheme, toastService]);

	const paletteMode = getStringValue(['palette', 'mode']) || 'light';

	return (
		<Stack sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
			<Paper square variant="outlined" sx={{ px: 2, pt: 1, flexShrink: 0 }}>
				<Stack direction="row" alignItems="center" spacing={1}>
					<Box sx={{ flex: 1 }}>
						<Typography variant="h6">{name || 'Theme'}</Typography>
						<Typography variant="caption" color="text.secondary">
							Customize the Material UI theme used by this app.
						</Typography>
					</Box>
					<CopyToClipboardButton value={JSON.stringify(theme, null, 2)} title="Copy theme JSON to clipboard" edge={false} />
					<PasteFromClipboardButton onClick={() => void handlePaste()} title="Paste theme JSON from clipboard" edge={false} />
				</Stack>
				<Tabs
					value={activeTab}
					onChange={(_, value: ThemeDesignerTab) => setActiveTab(value)}
					aria-label="Theme designer sections"
					variant="scrollable"
					scrollButtons="auto"
				>
					<Tab value="design" label="Design" />
					<Tab value="presets" label="Presets" />
					<Tab value="preview" label="Preview" />
				</Tabs>
			</Paper>
			{setName && (
				<Paper square variant="outlined" sx={{ px: 2, py: 1, flexShrink: 0 }}>
					<Stack direction="row" spacing={2} alignItems="center">
						<TextField label="Theme name" value={name ?? ''} onChange={(event) => setName(event.target.value)} size="small" fullWidth />
						<Button variant="outlined" onClick={setDefault} disabled={isDefault}>
							{isDefault ? 'Default' : 'Set as default'}
						</Button>
					</Stack>
				</Paper>
			)}

			{jsonError && (
				<Alert severity="error" onClose={() => setJsonError(undefined)} sx={{ mx: 2, mt: 2, flexShrink: 0 }}>
					{jsonError}
				</Alert>
			)}

			<Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
				<Stack spacing={2} sx={{ maxWidth: 1100, mx: 'auto' }}>
					{activeTab === 'presets' && (
						<Section title="Preset gallery" description="Ready-made visual directions for the application.">
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
									gap: 2,
								}}
							>
								{THEME_PRESETS.map((preset) => (
									<Paper
										key={preset.name}
										variant="outlined"
										sx={{
											display: 'grid',
											gridTemplateRows: '138px minmax(112px, 1fr) auto',
											minHeight: 312,
											overflow: 'hidden',
											borderRadius: 2,
											transition: 'box-shadow 160ms ease, border-color 160ms ease',
											'&:hover': { boxShadow: 3, borderColor: 'divider' },
										}}
									>
										<Box sx={{ p: 1.5, bgcolor: preset.preview.background }}>
											<Paper
												elevation={1}
												sx={{ height: '100%', overflow: 'hidden', borderRadius: 1, bgcolor: preset.preview.surface, color: preset.preview.text }}
											>
												<Box sx={{ height: 28, px: 1.25, display: 'flex', alignItems: 'center', bgcolor: preset.colors[0], color: 'common.white' }}>
													<Box sx={{ width: 50, height: 6, borderRadius: 1, bgcolor: 'rgba(255, 255, 255, 0.9)' }} />
												</Box>
												<Box sx={{ p: 1.25, display: 'grid', gridTemplateColumns: '0.7fr 1.3fr', gap: 1 }}>
													<Box sx={{ display: 'grid', alignContent: 'start', gap: 0.75 }}>
														<Box sx={{ width: '72%', height: 6, borderRadius: 1, bgcolor: preset.colors[0], opacity: 0.82 }} />
														<Box sx={{ width: '100%', height: 5, borderRadius: 1, bgcolor: preset.preview.mutedText, opacity: 0.22 }} />
														<Box sx={{ width: '86%', height: 5, borderRadius: 1, bgcolor: preset.preview.mutedText, opacity: 0.22 }} />
													</Box>
													<Box sx={{ display: 'grid', gap: 0.75 }}>
														<Box sx={{ width: '65%', height: 8, borderRadius: 1, bgcolor: preset.preview.text, opacity: 0.78 }} />
														<Box sx={{ height: 24, borderRadius: 1, bgcolor: preset.colors[1], opacity: 0.17 }} />
														<Box sx={{ width: '54%', height: 8, borderRadius: 1, bgcolor: preset.colors[0] }} />
													</Box>
												</Box>
											</Paper>
										</Box>
										<Stack spacing={1} sx={{ p: 1.75 }}>
											<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
												<Typography variant="subtitle1" fontWeight={600}>
													{preset.name}
												</Typography>
												<Stack direction="row" spacing={0.5} aria-label={`${preset.name} palette`}>
													{preset.colors.map((color) => (
														<Box key={color} sx={{ width: 16, height: 16, borderRadius: 1, bgcolor: color, border: 1, borderColor: 'divider' }} />
													))}
												</Stack>
											</Stack>
											<Typography variant="body2" color="text.secondary">
												{preset.description}
											</Typography>
										</Stack>
										<Box sx={{ px: 1.75, pb: 1.75 }}>
											<Button fullWidth size="small" variant="outlined" onClick={() => handleApplyPreset(preset)}>
												Apply preset
											</Button>
										</Box>
									</Paper>
								))}
							</Box>
						</Section>
					)}

					{activeTab === 'design' && (
						<>
							<Section title="Palette" description="Choose the color mode and semantic colors used by buttons, links, status messages, and other controls.">
								<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
									<TextField
										select
										label="Color mode"
										value={paletteMode}
										onChange={(event) => updateValue(['palette', 'mode'], event.target.value)}
										size="small"
									>
										<MenuItem value="light">Light</MenuItem>
										<MenuItem value="dark">Dark</MenuItem>
									</TextField>
									<TextField
										label="Contrast threshold"
										type="number"
										value={getNumberValue(['palette', 'contrastThreshold'])}
										onChange={(event) => updateValue(['palette', 'contrastThreshold'], event.target.value === '' ? undefined : Number(event.target.value))}
										inputProps={{ min: 1, max: 21, step: 0.1 }}
										size="small"
									/>
									<TextField
										label="Tonal offset"
										type="number"
										value={getNumberValue(['palette', 'tonalOffset'])}
										onChange={(event) => updateValue(['palette', 'tonalOffset'], event.target.value === '' ? undefined : Number(event.target.value))}
										inputProps={{ min: 0, max: 1, step: 0.05 }}
										size="small"
									/>
								</Box>
								<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
									{SEMANTIC_COLORS.map((colorName) => (
										<Paper key={colorName} variant="outlined" sx={{ p: 1.5 }}>
											<Stack spacing={1.5}>
												<Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
													{colorName}
												</Typography>
												{['main', 'light', 'dark', 'contrastText'].map((shade) => {
													const path = ['palette', colorName, shade];
													return (
														<ThemeColorField
															key={shade}
															label={shade === 'contrastText' ? 'Contrast text' : shade[0].toUpperCase() + shade.slice(1)}
															value={getStringValue(path)}
															onChange={(value) => updateValue(path, value)}
														/>
													);
												})}
											</Stack>
										</Paper>
									))}
								</Box>
							</Section>

							<Section title="Surfaces and text" description="Set page, panel, text, and divider colors.">
								<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
									{SURFACE_COLORS.map((field) => (
										<ThemeColorField
											key={field.path.join('.')}
											label={field.label}
											value={getStringValue(field.path)}
											onChange={(value) => updateValue(field.path, value)}
										/>
									))}
								</Box>
							</Section>

							<Section title="Interaction states" description="Set the colors used for hover, selected, focus, and disabled controls.">
								<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
									{ACTION_COLORS.map((field) => (
										<ThemeColorField
											key={field.path.join('.')}
											label={field.label}
											value={getStringValue(field.path)}
											onChange={(value) => updateValue(field.path, value)}
										/>
									))}
								</Box>
							</Section>

							<Section title="Typography" description="Control the base type scale, weights, and the most common text variants.">
								<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
									<TextField
										label="Font family"
										value={getStringValue(['typography', 'fontFamily'])}
										onChange={(event) => updateValue(['typography', 'fontFamily'], event.target.value)}
										placeholder="Roboto, Arial, sans-serif"
										size="small"
									/>
									<TextField
										label="Base font size"
										type="number"
										value={getNumberValue(['typography', 'fontSize'])}
										onChange={(event) => updateValue(['typography', 'fontSize'], event.target.value === '' ? undefined : Number(event.target.value))}
										inputProps={{ min: 8, max: 32, step: 1 }}
										size="small"
									/>
									<TextField
										label="Regular weight"
										type="number"
										value={getNumberValue(['typography', 'fontWeightRegular'])}
										onChange={(event) => updateValue(['typography', 'fontWeightRegular'], event.target.value === '' ? undefined : Number(event.target.value))}
										inputProps={{ min: 100, max: 900, step: 100 }}
										size="small"
									/>
									<TextField
										label="Medium weight"
										type="number"
										value={getNumberValue(['typography', 'fontWeightMedium'])}
										onChange={(event) => updateValue(['typography', 'fontWeightMedium'], event.target.value === '' ? undefined : Number(event.target.value))}
										inputProps={{ min: 100, max: 900, step: 100 }}
										size="small"
									/>
									<TextField
										label="Bold weight"
										type="number"
										value={getNumberValue(['typography', 'fontWeightBold'])}
										onChange={(event) => updateValue(['typography', 'fontWeightBold'], event.target.value === '' ? undefined : Number(event.target.value))}
										inputProps={{ min: 100, max: 900, step: 100 }}
										size="small"
									/>
								</Box>
								<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
									<TextField
										label="Default line height"
										value={getTextValue(['typography', 'lineHeight'])}
										onChange={(event) => updateValue(['typography', 'lineHeight'], event.target.value)}
										size="small"
									/>
									<TextField
										label="Default letter spacing"
										value={getTextValue(['typography', 'letterSpacing'])}
										onChange={(event) => updateValue(['typography', 'letterSpacing'], event.target.value)}
										size="small"
									/>
								</Box>
								<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
									{TYPOGRAPHY_VARIANTS.map((variant) => (
										<Paper key={variant.key} variant="outlined" sx={{ p: 1.5 }}>
											<Stack spacing={1.25}>
												<Typography variant="subtitle2">{variant.label}</Typography>
												<Stack direction="row" spacing={1}>
													<TextField
														label="Size"
														value={getTextValue(['typography', variant.key, 'fontSize'])}
														onChange={(event) => updateValue(['typography', variant.key, 'fontSize'], event.target.value)}
														size="small"
														fullWidth
													/>
													<TextField
														label="Weight"
														value={getTextValue(['typography', variant.key, 'fontWeight'])}
														onChange={(event) => updateValue(['typography', variant.key, 'fontWeight'], event.target.value)}
														size="small"
														fullWidth
													/>
												</Stack>
											</Stack>
										</Paper>
									))}
								</Box>
							</Section>

							<Section title="Layout and controls" description="Set shared layout measurements and button/input defaults.">
								<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
									<TextField
										label="Spacing unit"
										type="number"
										value={getNumberValue(['spacing'])}
										onChange={(event) => updateValue(['spacing'], event.target.value === '' ? undefined : Number(event.target.value))}
										inputProps={{ min: 1, max: 24, step: 1 }}
										size="small"
									/>
									<TextField
										label="Border radius"
										type="number"
										value={getNumberValue(['shape', 'borderRadius'])}
										onChange={(event) => updateValue(['shape', 'borderRadius'], event.target.value === '' ? undefined : Number(event.target.value))}
										inputProps={{ min: 0, max: 40, step: 1 }}
										size="small"
									/>
									<TextField
										select
										label="Direction"
										value={getStringValue(['direction']) || 'ltr'}
										onChange={(event) => updateValue(['direction'], event.target.value)}
										size="small"
									>
										<MenuItem value="ltr">Left to right</MenuItem>
										<MenuItem value="rtl">Right to left</MenuItem>
									</TextField>
								</Box>
								<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
									<Paper variant="outlined" sx={{ p: 1.5 }}>
										<Stack spacing={1.25}>
											<Typography variant="subtitle2">Buttons</Typography>
											<TextField
												select
												label="Text transform"
												value={getStringValue(['components', 'MuiButton', 'styleOverrides', 'root', 'textTransform']) || 'none'}
												onChange={(event) => updateValue(['components', 'MuiButton', 'styleOverrides', 'root', 'textTransform'], event.target.value)}
												size="small"
												fullWidth
											>
												<MenuItem value="none">None</MenuItem>
												<MenuItem value="uppercase">Uppercase</MenuItem>
												<MenuItem value="capitalize">Capitalize</MenuItem>
											</TextField>
											<FormControlLabel
												control={
													<Switch
														checked={getBooleanValue(['components', 'MuiButton', 'defaultProps', 'disableElevation'])}
														onChange={(event) => updateValue(['components', 'MuiButton', 'defaultProps', 'disableElevation'], event.target.checked)}
													/>
												}
												label="Disable elevation"
											/>
											<FormControlLabel
												control={
													<Switch
														checked={getBooleanValue(['components', 'MuiButtonBase', 'defaultProps', 'disableRipple'])}
														onChange={(event) => updateValue(['components', 'MuiButtonBase', 'defaultProps', 'disableRipple'], event.target.checked)}
													/>
												}
												label="Disable ripple"
											/>
										</Stack>
									</Paper>
									<Paper variant="outlined" sx={{ p: 1.5 }}>
										<Stack spacing={1.25}>
											<Typography variant="subtitle2">Inputs</Typography>
											<TextField
												select
												label="Default size"
												value={getStringValue(['components', 'MuiTextField', 'defaultProps', 'size']) || 'medium'}
												onChange={(event) => updateValue(['components', 'MuiTextField', 'defaultProps', 'size'], event.target.value)}
												size="small"
												fullWidth
											>
												<MenuItem value="small">Compact</MenuItem>
												<MenuItem value="medium">Comfortable</MenuItem>
											</TextField>
											<FormControlLabel
												control={
													<Switch
														checked={getBooleanValue(['components', 'MuiTextField', 'defaultProps', 'fullWidth'])}
														onChange={(event) => updateValue(['components', 'MuiTextField', 'defaultProps', 'fullWidth'], event.target.checked)}
													/>
												}
												label="Use full width by default"
											/>
										</Stack>
									</Paper>
								</Box>
							</Section>
						</>
					)}

					{activeTab === 'preview' && (
						<Section title="Preview" description="A larger interactive sample rendered with the current theme definition.">
							<ThemeProvider theme={previewTheme}>
								<Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, minHeight: { xs: 420, md: 560 }, bgcolor: 'background.default' }}>
									<Stack spacing={3} sx={{ height: '100%' }}>
										<Paper sx={{ p: { xs: 2, md: 3 } }}>
											<Typography variant="h4">Application theme</Typography>
											<Typography color="text.secondary" sx={{ mt: 0.75 }}>
												Preview text, surfaces, form controls, and actions with the active theme.
											</Typography>
										</Paper>
										<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.3fr) minmax(240px, 0.7fr)' }, gap: 2 }}>
											<Paper variant="outlined" sx={{ p: 2.5 }}>
												<Stack spacing={2}>
													<Typography variant="h6">Example form</Typography>
													<TextField label="Example field" defaultValue="Sample value" fullWidth />
													<TextField label="Supporting detail" placeholder="Add a description" multiline minRows={3} fullWidth />
													<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
														<Button variant="contained" color="primary">
															Primary action
														</Button>
														<Button variant="outlined" color="secondary">
															Secondary
														</Button>
													</Stack>
												</Stack>
											</Paper>
											<Stack spacing={2}>
												<Paper variant="outlined" sx={{ p: 2.5 }}>
													<Stack spacing={1}>
														<Typography variant="subtitle2">Status</Typography>
														<Alert severity="success">Changes saved</Alert>
														<Alert severity="info">Ready for review</Alert>
													</Stack>
												</Paper>
												<Paper variant="outlined" sx={{ p: 2.5, flex: 1 }}>
													<Typography variant="subtitle2">Visual tokens</Typography>
													<Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
														Typography, contrast, spacing, and component defaults are reflected here as the theme changes.
													</Typography>
												</Paper>
											</Stack>
										</Box>
									</Stack>
								</Paper>
							</ThemeProvider>
						</Section>
					)}
				</Stack>
			</Box>
		</Stack>
	);
}
